import { ButtonIcon, Skeleton, Text } from '@backstage/ui';
import { Icon } from '@iconify/react';
import { NewsEntityFilter } from '@ipen-portal/ipen-entity-model';
import { Card, useGetEntitiesByFilter } from '@ipen-portal/plugin-common-react';
import { useEffect, useState } from 'react';

import classes from './NewsHomeCard.module.css';

const newsCount = 4;
const imageHeight = 144;
const autoAdvanceMs = 6000;

export const NewsHomeCard = () => {
  const { data: allNews = [], status } = useGetEntitiesByFilter(NewsEntityFilter);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const news = allNews
    .toSorted((a, b) => b.spec.date.localeCompare(a.spec.date))
    .slice(0, newsCount);
  const currentIndex = Math.min(index, news.length - 1);
  const current = news[currentIndex];

  const showNext = () => setIndex((currentIndex + 1) % news.length);
  const showPrevious = () => setIndex((currentIndex - 1 + news.length) % news.length);

  useEffect(() => {
    if (paused || news.length < 2) {
      return;
    }
    const timeout = setTimeout(() => setIndex((currentIndex + 1) % news.length), autoAdvanceMs);
    return () => clearTimeout(timeout);
  }, [currentIndex, news.length, paused]);

  const pauseHandlers = {
    onBlur: () => setPaused(false),
    onFocus: () => setPaused(true),
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
  };

  return (
    <Card
      className={classes.root}
      footer={(
        <div className={classes.controls} {...pauseHandlers}>
          <ButtonIcon
            aria-label="Previous news"
            icon={<Icon icon="ri:arrow-left-s-line" />}
            isDisabled={news.length < 2}
            onPress={showPrevious}
            variant="primary"
          />
          <div className={classes.indicators}>
            {news.map((item, itemIndex) => (
              <button
                aria-current={itemIndex === currentIndex}
                aria-label={`Show news ${itemIndex + 1}: ${item.metadata.title ?? item.metadata.name}`}
                className={classes.indicator}
                key={item.metadata.uid ?? item.metadata.name}
                onClick={() => setIndex(itemIndex)}
                type="button"
              />
            ))}
          </div>
          <ButtonIcon
            aria-label="Next news"
            icon={<Icon icon="ri:arrow-right-s-line" />}
            isDisabled={news.length < 2}
            onPress={showNext}
            variant="primary"
          />
        </div>
      )}
    >
      {status === 'error' && <div className={classes.state} role="alert">News could not be loaded.</div>}
      {status === 'success' && !current && <div className={classes.state}>No news available.</div>}
      {status === 'pending' && (
        <div aria-busy="true" className={classes.viewport}>
          <div className={classes.slide}>
            <Skeleton className={classes.image} height={imageHeight} width="100%" />
            <div className={classes.text}>
              <Skeleton height={20} width="30%" />
              <Skeleton height={24} width="70%" />
              <Skeleton height={20} width="100%" />
              <Skeleton height={20} width="100%" />
              <Skeleton height={20} width="85%" />
            </div>
          </div>
        </div>
      )}
      {current && (
        <div className={classes.viewport} {...pauseHandlers}>
          <div className={classes.track} data-index={currentIndex}>
            {news.map((item, itemIndex) => (
              <a
                aria-hidden={itemIndex !== currentIndex}
                className={classes.slide}
                href={item.spec.link.url}
                key={item.metadata.uid ?? item.metadata.name}
                tabIndex={itemIndex === currentIndex ? undefined : -1}
              >
                <img alt="" className={classes.image} src={item.spec.image} />
                <div className={classes.text}>
                  <Text as="p" weight="bold">Whats new in iPEN?</Text>
                  <Text as="h3" className={classes.title} weight="bold">
                    {item.metadata.title ?? item.metadata.name}
                  </Text>
                  <Text as="p" className={classes.description} color="secondary">
                    {item.metadata.description}
                  </Text>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
